'use client';

import { GoogleLogin } from '@react-oauth/google';
import { useEffect, useRef, useState } from 'react';

/**
 * One-click Google button (Google Identity Services). Returns an ID token ("credential") that the backend
 * verifies directly, so there is no second popup click. It only works on origins authorised on the OAuth client,
 * so white-label domains keep using the popup bridge (see utils/googleOAuthBridge.ts).
 */
const CREDENTIAL_HOSTS = new Set(['admin.cresivox.com', 'localhost']);

export function useGoogleCredentialAvailable(): boolean {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    setOk(CREDENTIAL_HOSTS.has(window.location.hostname));
  }, []);
  return ok;
}

export function AuthGoogleCredentialButton({
  onCredential,
  onError,
  mode = 'signin',
  disabled = false,
}: {
  onCredential: (credential: string) => void;
  onError?: () => void;
  mode?: 'signin' | 'signup';
  disabled?: boolean;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(320);

  useEffect(() => {
    const w = wrapRef.current?.offsetWidth;
    if (w) setWidth(Math.max(200, Math.min(400, Math.floor(w))));
  }, []);

  return (
    <div
      ref={wrapRef}
      style={{ display: 'flex', justifyContent: 'center', width: '100%', opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
    >
      <GoogleLogin
        onSuccess={(res) => {
          if (res.credential) onCredential(res.credential);
          else onError?.();
        }}
        onError={() => onError?.()}
        text={mode === 'signup' ? 'signup_with' : 'continue_with'}
        theme="outline"
        size="large"
        shape="rectangular"
        logo_alignment="center"
        width={String(width)}
      />
    </div>
  );
}
